import React, { useMemo } from "react";

import { Branding, IntakeForm } from "@smart/bridge-intake-components-dom";
import {
  FieldNS,
  FormNS,
  SectionNS,
  SubmissionNS,
} from "@smart/bridge-types-basic";
import { isNotNullOrUndefined } from "@smart/itops-utils-basic";

import { BrandingInput } from "../types";

const convertToBranding = (branding?: BrandingInput): Branding => ({
  brandColor: branding?.brandColor || undefined,
  affectedAreas: {
    headerBackground: branding?.affectedAreas?.headerBackground || false,
    headerUnderline: branding?.affectedAreas?.headerUnderline || false,
    firmName: branding?.affectedAreas?.firmName || false,
    sectionTabHighlights:
      branding?.affectedAreas?.sectionTabHighlights || false,
    sectionHeadings: branding?.affectedAreas?.sectionHeadings || false,
    buttons: branding?.affectedAreas?.buttons || false,
  },
  headerTextColor: branding?.headerTextColor || "default",
  backgroundColor: branding?.backgroundColor || "default",
  headerDisplay: {
    firmLogo: isNotNullOrUndefined(branding?.headerDisplay?.firmLogo)
      ? branding?.headerDisplay?.firmLogo
      : true,
    firmName: isNotNullOrUndefined(branding?.headerDisplay?.firmName)
      ? branding?.headerDisplay?.firmName
      : true,
  },
  buttonRadius: branding?.buttonRadius || undefined,
  inputRadius: branding?.inputRadius || "moderatelyRounded",
  logo: branding?.logo || undefined,
});

export type BrandingPreviewFormContainerProps = {
  branding?: BrandingInput;
  containerRef: React.RefObject<HTMLDivElement | null>;
  team:
    | {
        uri: string;
        name: string;
        picture?: string | null;
      }
    | undefined;
  firmLogoDataUrl?: string | null;
};

export const BrandingPreviewFormContainer = ({
  branding,
  containerRef,
  team,
  firmLogoDataUrl,
}: BrandingPreviewFormContainerProps) => {
  const previewPicture = firmLogoDataUrl || team?.picture;
  const formTemplate = useMemo(() => {
    const contactDetailsSectionUri = SectionNS.generateUri();
    const matterDetailsSectionUri = SectionNS.generateUri();
    const additionalContactsSectionUri = SectionNS.generateUri();

    const sections = [
      {
        uri: contactDetailsSectionUri,
        title: "Contact Details",
        order: "a",
      },
      {
        uri: matterDetailsSectionUri,
        title: "Matter Details",
        order: "b",
      },
      {
        uri: additionalContactsSectionUri,
        title: "Additional Contacts",
        order: "c",
      },
    ];

    const fields = [
      {
        uri: FieldNS.generateUri(),
        sectionUri: contactDetailsSectionUri,
        label: "First Name",
        hint: "",
        order: "a",
        type: "text" as const,
        options: [],
        mandatory: true,
      },
      {
        uri: FieldNS.generateUri(),
        sectionUri: contactDetailsSectionUri,
        label: "Last Name",
        hint: "",
        order: "b",
        type: "text" as const,
        options: [],
        mandatory: false,
      },
      {
        uri: FieldNS.generateUri(),
        sectionUri: contactDetailsSectionUri,
        label: "Phone Number",
        hint: "",
        order: "c",
        type: "phoneNumber" as const,
        options: [],
        mandatory: false,
      },
      {
        uri: FieldNS.generateUri(),
        sectionUri: contactDetailsSectionUri,
        label: "Email",
        hint: "",
        order: "d",
        type: "email" as const,
        options: [],
        mandatory: true,
      },
      {
        uri: FieldNS.generateUri(),
        sectionUri: contactDetailsSectionUri,
        label: "Upload any relevant documents",
        hint: "",
        order: "e",
        type: "file" as const,
        options: [],
        mandatory: false,
      },
    ];

    return {
      sections,
      fields,
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="[&_*]:transition-none absolute w-[1280px] h-[960px] pointer-events-none z-[-1]"
    >
      <IntakeForm
        form={{
          uri: FormNS.generateUri(),
          team: {
            uri: team?.uri || "",
            name: team?.name || "",
            picture: previewPicture,
          },
        }}
        sections={formTemplate.sections}
        groups={[]}
        fields={formTemplate.fields}
        lookup={{
          addressLookup: {
            search: () => ({ result: undefined, loading: false }),
            load: async () => null,
          },
          fileLookup: {
            load: async () => undefined,
            upload: () => ({
              promise: Promise.resolve(),
              abort: () => {},
            }),
          },
        }}
        submission={{
          uri: SubmissionNS.generateUri(),
        }}
        staffDetails={[]}
        loadResponses={async () => undefined}
        submitResponses={async () => ({
          operationIds: [],
          submissionStatus: {},
        })}
        branding={convertToBranding(branding)}
      />
    </div>
  );
};
